import React, { createContext, useContext, useMemo } from "react";

import { useForm, UseFormReturn } from "react-hook-form";

import useAdvisorClientsV2 from "@fartherfinance/frontend/api/Entity/hooks/useAdvisorClientsV2";
import useGetHousehold from "@fartherfinance/frontend/api/Households/hooks/useGetHousehold";
import {
  HouseholdId,
  HouseholdWithBalances,
} from "@fartherfinance/frontend/api/Households/types";

import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import { useClientsOptions } from "@src/multiCustodian/pages/Advisor/Tasks/hooks";
import { formatPersonName } from "@src/multiCustodian/pages/Advisor/utils";

import { HouseholdFormWithMembers } from "./types";
import { formatHouseholdDefaultFormFields } from "./utils";

interface HouseholdContextProps {
  householdId: HouseholdId;
  household?: HouseholdWithBalances;
  form: UseFormReturn<HouseholdFormWithMembers>;
  isLoading: boolean;
  clientsOptions: ReturnType<typeof useClientsOptions>["clientsOptions"];
  isLoadingClients: boolean;
  onSave?: (formData: HouseholdFormWithMembers) => Promise<void>;
  onCancel?: () => void;
}

const HouseholdContext = createContext<HouseholdContextProps | undefined>(
  undefined
);

interface HouseholdProviderProps {
  householdId: HouseholdId;
  onSave?: (formData: HouseholdFormWithMembers) => Promise<void>;
  onCancel?: () => void;
}

export const HouseholdProvider: React.FC<
  React.PropsWithChildren<HouseholdProviderProps>
> = ({ householdId, onSave, onCancel, children }) => {
  const auth = useAdvisorRequestAuth();

  const { data: householdData, isLoading } = useGetHousehold(householdId, auth);

  const { data: advisorClientsResponseData, isLoading: isLoadingClients } =
    useAdvisorClientsV2(householdData?.data.advisorId || null, auth);

  const clientsOptions = useMemo(() => {
    if (!advisorClientsResponseData) {
      return [];
    }
    return advisorClientsResponseData.clients.map((client) => ({
      label: formatPersonName(client.name),
      value: client.clientId,
    }));
  }, [advisorClientsResponseData]);

  const form = useForm<HouseholdFormWithMembers>({
    mode: "onChange",
    values: formatHouseholdDefaultFormFields(householdData?.data),
  });

  return (
    <HouseholdContext.Provider
      value={{
        householdId,
        household: householdData?.data,
        isLoading,
        form,
        clientsOptions,
        isLoadingClients,
        onSave,
        onCancel,
      }}
    >
      {children}
    </HouseholdContext.Provider>
  );
};

export const useHouseholdContext = () => {
  const context = useContext(HouseholdContext);

  if (!context) {
    throw new Error("useHouseholdContext must be used within HouseholdContext");
  }

  return context;
};
